import React, { useEffect, useState } from 'react';
import { api } from '../api';

const fmt = (n) => parseFloat(n || 0).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' }) : '—';
const isOverdue = (due, status) => due && new Date(due) < new Date() && status !== 'payée' && status !== 'paid';

const STATUS_CONFIG = {
  payée: { label: 'Payée', color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0' },
  paid:  { label: 'Payée', color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0' },
  envoyée: { label: 'En attente', color: '#d97706', bg: '#fffbeb', border: '#fde68a' },
  sent:    { label: 'En attente', color: '#d97706', bg: '#fffbeb', border: '#fde68a' },
  brouillon: { label: 'Brouillon', color: '#64748b', bg: '#f8fafc', border: '#e2e8f0' },
  draft:     { label: 'Brouillon', color: '#64748b', bg: '#f8fafc', border: '#e2e8f0' },
  annulée:   { label: 'Annulée', color: '#dc2626', bg: '#fef2f2', border: '#fecaca' },
};

function CopyButton({ text, label = 'Copier' }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={() => { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
      style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', padding: '0.45rem 0.9rem', borderRadius: '0.5rem', border: '1px solid #d1d5db', background: copied ? '#f0fdf4' : 'white', color: copied ? '#16a34a' : '#374151', cursor: 'pointer', fontSize: '0.82rem', fontWeight: '600', transition: 'all 0.15s' }}
    >
      {copied ? '✅' : '📋'} {copied ? 'Copié !' : label}
    </button>
  );
}

function DetailRow({ label, value, mono = false, highlight = false }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.65rem 0', borderBottom: '1px solid #f1f5f9' }}>
      <span style={{ color: '#64748b', fontSize: '0.85rem' }}>{label}</span>
      <span style={{ color: highlight ? '#0f172a' : '#374151', fontWeight: highlight ? '700' : '500', fontFamily: mono ? 'monospace' : 'inherit', fontSize: highlight ? '1.05rem' : '0.9rem' }}>{value}</span>
    </div>
  );
}

export default function PayInvoice() {
  const token = window.location.pathname.split('/pay/')[1];
  const [invoice, setInvoice] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) { setError('Lien invalide'); setLoading(false); return; }
    api.getPublicInvoice(token)
      .then(d => {
        if (d.error) setError(d.error);
        else setInvoice(d.invoice);
        setLoading(false);
      })
      .catch(() => { setError('Erreur de connexion au serveur'); setLoading(false); });
  }, []);

  if (loading) return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ width: '40px', height: '40px', border: '3px solid #e2e8f0', borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 1rem' }} />
        <p style={{ color: '#94a3b8', fontSize: '0.9rem' }}>Chargement de la facture…</p>
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );

  if (error) return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem' }}>
      <div style={{ background: 'white', borderRadius: '1rem', padding: '3rem 2.5rem', textAlign: 'center', maxWidth: '420px', boxShadow: '0 4px 24px rgba(0,0,0,0.06)' }}>
        <div style={{ fontSize: '3.5rem', marginBottom: '1rem' }}>🔗</div>
        <h2 style={{ margin: '0 0 0.5rem', color: '#0f172a', fontSize: '1.25rem' }}>Lien invalide ou expiré</h2>
        <p style={{ color: '#64748b', margin: 0, lineHeight: '1.6' }}>{error}</p>
      </div>
    </div>
  );

  const inv = invoice;
  const status = inv.status || 'draft';
  const statusCfg = STATUS_CONFIG[status] || STATUS_CONFIG.draft;
  const paid = status === 'payée' || status === 'paid';
  const overdue = isOverdue(inv.due_date, status);
  // Stripe URL only — évite la boucle si payment_link_url = l'URL de la page courante
  const hasStripe = !!inv.payment_link_url && inv.payment_link_url.includes('stripe.com');
  const hasIBAN = !!inv.iban;

  // Calcul montants
  const amountHT = parseFloat(inv.amount_ht || 0);
  const amountTTC = parseFloat(inv.amount_ttc || inv.amount || 0);
  const tvaRate = parseFloat(inv.tva_rate || 0);
  const tvaAmount = parseFloat(inv.tva_amount || (amountTTC - amountHT) || 0);
  const showBreakdown = amountHT > 0 && tvaRate > 0;

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(145deg, #f0f4ff 0%, #f8fafc 60%, #fef9f0 100%)', padding: '2rem 1rem', fontFamily: "'Inter', 'Segoe UI', system-ui, sans-serif" }}>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
        @media print {
          body { background: white !important; }
          .no-print { display: none !important; }
          .invoice-card { box-shadow: none !important; border: 1px solid #e2e8f0 !important; }
        }
        @media (max-width: 600px) {
          .invoice-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>

      <div className="invoice-card" style={{ maxWidth: '680px', margin: '0 auto', background: 'white', borderRadius: '1.25rem', boxShadow: '0 8px 40px rgba(0,0,0,0.08)', overflow: 'hidden' }}>

        {/* ── Bandeau statut ── */}
        {paid && (
          <div style={{ background: '#16a34a', color: 'white', padding: '0.65rem 1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.87rem', fontWeight: '600' }}>
            <span>✅</span> Cette facture a été réglée — Merci pour votre paiement
          </div>
        )}
        {overdue && !paid && (
          <div style={{ background: '#dc2626', color: 'white', padding: '0.65rem 1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.87rem', fontWeight: '600' }}>
            <span>⚠️</span> Facture en retard de paiement depuis le {fmtDate(inv.due_date)}
          </div>
        )}

        {/* ── En-tête artisan ── */}
        <div style={{ padding: '2rem 2.5rem 1.5rem', borderBottom: '1px solid #f1f5f9' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1.5rem' }}>

            {/* Identité prestataire */}
            <div>
              {inv.logo_url && (
                <img src={inv.logo_url} alt="Logo" style={{ height: '48px', marginBottom: '0.75rem', objectFit: 'contain' }} />
              )}
              <div style={{ fontSize: '1.3rem', fontWeight: '800', color: '#0f172a', lineHeight: '1.2' }}>
                {inv.nom_entreprise || inv.artisan_name || 'Prestataire'}
              </div>
              <div style={{ marginTop: '0.6rem', color: '#64748b', fontSize: '0.82rem', lineHeight: '1.8' }}>
                {inv.artisan_adresse && <div>{inv.artisan_adresse}</div>}
                {(inv.artisan_cp || inv.artisan_ville) && <div>{[inv.artisan_cp, inv.artisan_ville].filter(Boolean).join(' ')}</div>}
                {inv.artisan_tel && <div>📞 {inv.artisan_tel}</div>}
                {inv.artisan_email && <div>✉ {inv.artisan_email}</div>}
                {inv.siret && <div style={{ marginTop: '0.25rem', fontWeight: '500', color: '#475569' }}>SIRET {inv.siret}</div>}
                {inv.tva_number && <div style={{ fontWeight: '500', color: '#475569' }}>N° TVA {inv.tva_number}</div>}
              </div>
            </div>

            {/* Numéro + statut */}
            <div style={{ textAlign: 'right' }}>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', padding: '0.35rem 0.9rem', borderRadius: '2rem', background: statusCfg.bg, border: `1px solid ${statusCfg.border}`, color: statusCfg.color, fontWeight: '700', fontSize: '0.82rem', marginBottom: '0.75rem' }}>
                <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: statusCfg.color, display: 'inline-block' }} />
                {statusCfg.label}
              </div>
              <div style={{ color: '#94a3b8', fontSize: '0.75rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Facture</div>
              <div style={{ color: '#0f172a', fontSize: '1.4rem', fontWeight: '800' }}>{inv.number}</div>
            </div>
          </div>
        </div>

        {/* ── Infos facture ── */}
        <div className="invoice-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0', borderBottom: '1px solid #f1f5f9' }}>

          {/* Client */}
          <div style={{ padding: '1.5rem 2.5rem', borderRight: '1px solid #f1f5f9' }}>
            <div style={{ color: '#94a3b8', fontSize: '0.72rem', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '0.6rem' }}>Facturé à</div>
            <div style={{ color: '#0f172a', fontWeight: '700', fontSize: '0.95rem' }}>{inv.client_name || '—'}</div>
            {inv.client_email && <div style={{ color: '#64748b', fontSize: '0.82rem', marginTop: '2px' }}>{inv.client_email}</div>}
            {inv.client_address && <div style={{ color: '#64748b', fontSize: '0.82rem', marginTop: '2px' }}>{inv.client_address}</div>}
            {inv.client_siret && <div style={{ color: '#94a3b8', fontSize: '0.78rem', marginTop: '4px' }}>SIRET {inv.client_siret}</div>}
          </div>

          {/* Dates */}
          <div style={{ padding: '1.5rem 2.5rem' }}>
            <div style={{ color: '#94a3b8', fontSize: '0.72rem', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '0.6rem' }}>Dates</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <div>
                <span style={{ color: '#64748b', fontSize: '0.78rem' }}>Émise le </span>
                <span style={{ color: '#0f172a', fontWeight: '600', fontSize: '0.85rem' }}>{fmtDate(inv.date || inv.created_at)}</span>
              </div>
              {inv.due_date && (
                <div>
                  <span style={{ color: '#64748b', fontSize: '0.78rem' }}>Échéance </span>
                  <span style={{ color: overdue && !paid ? '#dc2626' : '#0f172a', fontWeight: '600', fontSize: '0.85rem' }}>{fmtDate(inv.due_date)}</span>
                </div>
              )}
              {inv.payment_terms && (
                <div>
                  <span style={{ color: '#64748b', fontSize: '0.78rem' }}>Conditions </span>
                  <span style={{ color: '#475569', fontSize: '0.82rem' }}>{inv.payment_terms}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Description / Prestations ── */}
        {inv.notes && (
          <div style={{ padding: '1.5rem 2.5rem', borderBottom: '1px solid #f1f5f9' }}>
            <div style={{ color: '#94a3b8', fontSize: '0.72rem', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '0.75rem' }}>Description des prestations</div>
            <div style={{ background: '#f8fafc', borderRadius: '0.75rem', padding: '1rem 1.25rem', color: '#374151', fontSize: '0.88rem', lineHeight: '1.7', whiteSpace: 'pre-wrap' }}>
              {inv.notes}
            </div>
          </div>
        )}

        {/* ── Montants ── */}
        <div style={{ padding: '1.5rem 2.5rem', borderBottom: '1px solid #f1f5f9' }}>
          <div style={{ color: '#94a3b8', fontSize: '0.72rem', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '0.5rem' }}>Récapitulatif</div>
          <div style={{ background: '#f8fafc', borderRadius: '0.75rem', padding: '0.25rem 1.25rem' }}>
            {showBreakdown && (
              <>
                <DetailRow label="Montant HT" value={fmt(amountHT)} />
                <DetailRow label={`TVA (${tvaRate}%)`} value={fmt(tvaAmount)} />
              </>
            )}
            <DetailRow
              label="Total TTC"
              value={fmt(amountTTC)}
              highlight
            />
          </div>
        </div>

        {/* ── Section paiement ── */}
        <div style={{ padding: '1.75rem 2.5rem' }}>

          {paid ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '0.875rem', padding: '1.25rem 1.5rem' }}>
              <div style={{ fontSize: '2rem' }}>✅</div>
              <div>
                <div style={{ color: '#15803d', fontWeight: '700', fontSize: '0.95rem' }}>Facture réglée</div>
                <div style={{ color: '#4ade80', fontSize: '0.82rem', marginTop: '2px' }}>Aucun paiement en attente</div>
              </div>
            </div>
          ) : (
            <>
              <div style={{ color: '#94a3b8', fontSize: '0.72rem', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '1rem' }}>Modalités de paiement</div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>

                {/* Paiement Stripe */}
                {hasStripe && (
                  <a
                    href={inv.payment_link_url}
                    target="_blank"
                    rel="noreferrer"
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.6rem', background: 'linear-gradient(135deg, #635bff, #4f46e5)', color: 'white', borderRadius: '0.75rem', padding: '1rem 1.5rem', textDecoration: 'none', fontWeight: '700', fontSize: '0.95rem', boxShadow: '0 4px 14px rgba(99,91,255,0.35)', transition: 'all 0.2s' }}
                  >
                    💳 Payer en ligne — {fmt(amountTTC)}
                  </a>
                )}

                {/* Virement IBAN */}
                {hasIBAN && (
                  <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '0.875rem', padding: '1.25rem 1.5rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
                      <div style={{ width: '28px', height: '28px', background: '#dbeafe', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.85rem' }}>🏦</div>
                      <span style={{ color: '#1e40af', fontWeight: '700', fontSize: '0.87rem' }}>Virement bancaire</span>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', marginBottom: '1rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
                        <span style={{ color: '#64748b', fontSize: '0.8rem', fontWeight: '600' }}>IBAN</span>
                        <span style={{ color: '#0f172a', fontFamily: 'monospace', fontSize: '0.85rem', letterSpacing: '0.04em', wordBreak: 'break-all' }}>{inv.iban}</span>
                      </div>
                      {inv.bic && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ color: '#64748b', fontSize: '0.8rem', fontWeight: '600' }}>BIC / SWIFT</span>
                          <span style={{ color: '#0f172a', fontFamily: 'monospace', fontSize: '0.85rem' }}>{inv.bic}</span>
                        </div>
                      )}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ color: '#64748b', fontSize: '0.8rem', fontWeight: '600' }}>Référence</span>
                        <span style={{ color: '#0f172a', fontFamily: 'monospace', fontSize: '0.85rem', fontWeight: '700' }}>{inv.number}</span>
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                      <CopyButton text={inv.iban} label="Copier l'IBAN" />
                      <CopyButton text={inv.number} label="Copier la référence" />
                    </div>
                  </div>
                )}

                {/* Aucun moyen de paiement configuré */}
                {!hasStripe && !hasIBAN && (
                  <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '0.875rem', padding: '1.25rem 1.5rem', textAlign: 'center' }}>
                    <p style={{ color: '#64748b', margin: '0 0 0.25rem', fontSize: '0.87rem' }}>
                      Contactez l'émetteur pour les modalités de paiement.
                    </p>
                    {inv.artisan_email && (
                      <a href={`mailto:${inv.artisan_email}`} style={{ color: '#3b82f6', fontWeight: '600', fontSize: '0.85rem' }}>
                        {inv.artisan_email}
                      </a>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* ── Footer ── */}
        <div style={{ background: '#f8fafc', borderTop: '1px solid #f1f5f9', padding: '1rem 2.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
          <span style={{ color: '#94a3b8', fontSize: '0.75rem' }}>
            Facture {inv.number} émise par {inv.nom_entreprise || 'le prestataire'}
          </span>
          <span style={{ color: '#cbd5e1', fontSize: '0.72rem' }}>
            Propulsé par{' '}
            <a href="https://app.akodigitale.fr" target="_blank" rel="noreferrer" style={{ color: '#93c5fd', textDecoration: 'none', fontWeight: '600' }}>
              FacturArt
            </a>
          </span>
        </div>
      </div>
    </div>
  );
}
